import { useEffect, useCallback, useState, useRef } from "react";
import { FiSearch, FiX, FiUsers, FiPhone } from "react-icons/fi";
import Pagination from "@mui/material/Pagination";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useReduxProject } from "@Hooks/reduxProjectHook/useReduxProject";
import useDebounce from "@Hooks/useDebounce";
import ErrorHandler from "@Root/ErrorHandler";
import CampaignRunService from '@Helpers/URLHelpers/WeboutService/CampaignRunService';
import NameCardService from "@Helpers/URLHelpers/WeboutService/NameCardService";
import "./CampaignRecipientsView.scss";

function CampaignRecipientsView({ onClose, campaign, reloadData }) {


    const { currentProjectId } = useReduxProject();
    const [cursor, setCursor] = useState("auto");
    const [page, setPage] = useState(1);
    const [size] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalResult, setTotalResult] = useState(0);
    const [nameCardList, setNameCardList] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedCustomers, setSelectedCustomers] = useState([]);

    const debouncedSearch = useDebounce(search, 500, true);
    const nameCardListRef = useRef(null);

    const nameCardService = new NameCardService();
    const campaignRunService = new CampaignRunService();
    const errorHandler = new ErrorHandler();

    const canEdit = campaign?.status !== 'completed';

    let sendTo = JSON.parse(campaign?.sendTo);

    const buildListFilter = useCallback(
        (pageNumber = 0, pageSize = size) => ({
            nameCardFilter: {
                searchQuery: debouncedSearch,
                projectIdList: [currentProjectId],
                idList: [...sendTo],
                activeList: [true],
                sortBy: "createTimeStamp",
                sortAsc: false,
            },
            page: debouncedSearch === "" ? pageNumber : 0,
            size: pageSize,
        }),
        [debouncedSearch, currentProjectId, size]
    );

    useEffect(() => {
        if (debouncedSearch !== "") {
            setPage(1);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        fetchNameCards(page - 1, size);
    }, [page, size, debouncedSearch]);

    const fetchNameCards = async (apiPage = page - 1, pageSize = size) => {
        setCursor("wait");

        try {
            const body = buildListFilter(apiPage, pageSize);
            let response = await nameCardService.getListFiltered(body);
            response = JSON.parse(response.body);

            setNameCardList(response?.result?.nameCardList || []);
            setTotalPages(response?.result?.totalPages || 0);
            setTotalResult(response?.result?.totalItems || 0);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setCursor("auto");
        }
    };

    const onSubmit = async () => {
        if (!selectedCustomers.length) return;
        setCursor("wait");
        try {
            const bodyObj = {
                campaignId: campaign?.id,
                removeIdList: [...selectedCustomers],
                projectId: currentProjectId,
            };
            const response = await campaignRunService.removeRecipients(bodyObj);
            if (response.status < 400) {
                await reloadData();
                onClose();
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setCursor("auto");
        }
    };

    const avatarStyle = (name = "") => {
        const palettes = [
            { bg: "#E0EDFF", color: "#2563EB" },
            { bg: "#DCFCE7", color: "#15803D" },
            { bg: "#FEF3C7", color: "#B45309" },
            { bg: "#EDE9FE", color: "#7C3AED" },
            { bg: "#FCE7F3", color: "#DB2777" },
            { bg: "#FFEDD5", color: "#EA580C" },
        ];

        return palettes[(name.charCodeAt(0) || 0) % palettes.length];
    };

    const getItemName = (item) => {
        const avatar = avatarStyle(item.name);

        return (
            <div className="contact-name-cell">
                <div
                    className="contact-avatar"
                    style={{
                        backgroundColor: avatar.bg,
                        color: avatar.color,
                    }}
                >
                    {(item.name || "?").charAt(0).toUpperCase()}
                </div>

                <div className="contact-info">
                    <span className="contact-name">
                        {item.name || "Unnamed Contact"}
                    </span>
                </div>
            </div>
        );
    };

    const getPhoneNumber = (item) => {
        return (
            <div className="contact-phone">
                <FiPhone />
                <span>{item.contactNumber || "—"}</span>
            </div>
        );
    };

    const onSelect = (e) => {
        const currentPageIds = nameCardList.map((item) => item.id);
        const selectedIdsOnCurrentPage = e.value.map((item) => item.id);

        setSelectedCustomers((prev) => {
            const remaining = prev.filter(
                (id) => !currentPageIds.includes(id)
            );

            return [...remaining, ...selectedIdsOnCurrentPage];
        });
    };

    const clearSearch = () => {
        setSearch("");
        setPage(1);
    };

    const emptyList = () => {
        const hasSearch = search.trim().length > 0;

        return (
            <div className="empty-contacts">
                <div className="empty-contacts-icon">
                    {hasSearch ? <FiSearch /> : <FiUsers />}
                </div>

                <h4>
                    {hasSearch
                        ? "No contacts found"
                        : "No contacts available"}
                </h4>

                <p>
                    {hasSearch
                        ? `We couldn't find any contacts matching "${search}".`
                        : "There are no contacts available."}
                </p>

                {hasSearch && (
                    <button
                        type="button"
                        className="empty-clear-btn"
                        onClick={clearSearch}
                    >
                        Clear search
                    </button>
                )}
            </div>
        );
    };

    const getFooter = () => {
        return (
            <div className="contacts-footer">
                <div className="footer-left">
                    {totalResult > 0 && (
                        <span className="result-count">
                            {totalResult}{" "}
                            {totalResult === 1 ? "contact" : "contacts"}
                        </span>
                    )}

                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            shape="rounded"
                            size="small"
                        />
                    )}
                </div>

                <div className="footer-actions">
                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={onClose}
                        disabled={cursor === "wait"}
                    >
                        Cancel
                    </button>

                    {canEdit &&
                    <button
                        type="button"
                        className="submit-btn"
                        onClick={onSubmit}
                        disabled={
                            cursor === "wait" ||
                            selectedCustomers.length === 0
                        }
                    >
                        {cursor === "wait" ? (
                            <>
                                <span className="button-loader" />
                                Removing...
                            </>
                        ) : (
                            <>
                                Remove Contacts
                                {selectedCustomers.length > 0 && (
                                    <span className="selected-badge">
                                        {selectedCustomers.length}
                                    </span>
                                )}
                            </>
                        )}
                    </button>
                    }
                </div>
            </div>
        );
    };

    return (
        <div
            className="campaign-recipients-overlay"
            style={{ cursor }}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="campaign-recipients-modal"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="modal-header">
                    <div className="modal-title-wrapper">
                        <div className="modal-title-icon">
                            <FiUsers />
                        </div>

                        <div>
                            <h2>{canEdit ? 'Remove' : 'List'} Contacts</h2>
                            {canEdit ?
                                <p>
                                    Select contacts you want to remove from this item.
                                </p>
                            :
                                <p>
                                    Contact list's from this item.
                                </p>
                            }
                        </div>
                    </div>

                    <button
                        type="button"
                        className="close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </div>

                {/* Search */}
                <div className="search-section">
                    <div
                        className={`search-box ${
                            search ? "has-value" : ""
                        }`}
                    >
                        <FiSearch className="search-icon" />

                        <input
                            type="text"
                            placeholder="Search by name or phone number..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                        {search && (
                            <button
                                type="button"
                                className="clear-search-btn"
                                onClick={clearSearch}
                                aria-label="Clear search"
                            >
                                <FiX />
                            </button>
                        )}
                    </div>

                    {selectedCustomers.length > 0 && (
                        <div className="selection-info">
                            <span className="selection-dot" />
                            {selectedCustomers.length} selected
                        </div>
                    )}
                </div>

                {/* Table */}
                <div className="contacts-list">
                    <DataTable
                        value={nameCardList}
                        ref={nameCardListRef}
                        scrollable
                        scrollHeight="flex"
                        style={{ height: "100%" }}
                        columnResizeMode="expand"
                        selection={nameCardList.filter((item) =>
                            selectedCustomers.includes(item.id)
                        )}
                        onSelectionChange={canEdit ? onSelect : null}
                        emptyMessage={emptyList}
                        footer={getFooter}
                        resizableColumns
                        dataKey="id"
                        className="contacts-table"
                    >
                        {canEdit &&
                        <Column
                            selectionMode="multiple"
                            headerStyle={{
                                width: "56px",
                                paddingLeft: "20px",
                            }}
                            bodyStyle={{
                                width: "56px",
                                paddingLeft: "20px",
                            }}
                        />
                        }

                        <Column
                            field="name"
                            header="CONTACT"
                            body={getItemName}
                            style={{ minWidth: "260px" }}
                        />

                        <Column
                            field="number"
                            header="PHONE NUMBER"
                            body={getPhoneNumber}
                            style={{ minWidth: "220px" }}
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
}

export default CampaignRecipientsView;